import { Formik, FormikHelpers } from "formik";
import { FC, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import yup from "app/util/yup";
import { useReportEngagementOverviewQuery, useUpdateReportEngagementOverviewMutation } from "app/api/graph/types";
import { reportEngagementOverviewGraphId } from "app/api/graph/helpers";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";

type Props = {
    engagementOverviewId: number;
    onUpdate?: () => void;
};

type FormValues = {
    objectives: string;
    rulesOfEngagement: string;
    limitationsAndExclusions: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    objectives: yup.string().required(),
    rulesOfEngagement: yup.string().required(),
    limitationsAndExclusions: yup.string().required(),
});

export const EditReportEngagementOverviewForm: FC<Props> = ({ engagementOverviewId, onUpdate }) => {
    const { data, loading, error } = useReportEngagementOverviewQuery({
        variables: { id: reportEngagementOverviewGraphId(engagementOverviewId) },
    });
    const engagementOverview = data?.reportEngagementOverview;
    const [updateEngagementOverview] = useUpdateReportEngagementOverviewMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            objectives: engagementOverview?.objectives ?? "",
            rulesOfEngagement: engagementOverview?.rulesOfEngagement ?? "",
            limitationsAndExclusions: engagementOverview?.limitationsAndExclusions ?? "",
        }),
        [engagementOverview],
    );

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!engagementOverview) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateEngagementOverview({ variables: { input: { id: engagementOverview.id, ...values } } })
            .then(() => onUpdate?.())
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, setFieldTouched, values, isSubmitting, errors, touched, status }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />
                    <Form.Group className="mb-3">
                        <Form.Label>Objectives</Form.Label>
                        <RichTextEditor
                            value={values.objectives ?? ""}
                            onChange={(value) => setFieldValue("objectives", value)}
                            onBlur={() => setFieldTouched("objectives")}
                            isInvalid={touched.objectives && !!errors.objectives}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.objectives}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Rules of engagement</Form.Label>
                        <RichTextEditor
                            value={values.rulesOfEngagement ?? ""}
                            onChange={(value) => setFieldValue("rulesOfEngagement", value)}
                            onBlur={() => setFieldTouched("rulesOfEngagement")}
                            isInvalid={touched.rulesOfEngagement && !!errors.rulesOfEngagement}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.rulesOfEngagement}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Limitations and exclusions</Form.Label>
                        <RichTextEditor
                            value={values.limitationsAndExclusions ?? ""}
                            onChange={(value) => setFieldValue("limitationsAndExclusions", value)}
                            onBlur={() => setFieldTouched("limitationsAndExclusions")}
                            isInvalid={touched.limitationsAndExclusions && !!errors.limitationsAndExclusions}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.limitationsAndExclusions}</Form.Control.Feedback>
                    </Form.Group>
                    <Button type="submit" disabled={isSubmitting}>
                        Update
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
